import BlockType from 'scratch-vm/src/extension-support/block-type';
import BlockShapeRegistry from 'scratch-vm/src/engine/block-shape-registry';
import {injectExtensionBlockTheme} from '../themes/blockHelpers';

/**
 * Register a variadic ("extendable") block type in Blockly for an extension.
 *
 * Called from blocks.jsx whenever an extension block has blockInfo.isExtendable.
 * Delegates to ScratchBlocks.defineExtendableBlock() which must exist in the
 * loaded scratch-blocks build.
 *
 * @param {object} ScratchBlocks  - The ScratchBlocks namespace.
 * @param {object} categoryInfo   - Extension category info (colors, id, icon…).
 * @param {object} blockInfoEntry - {info, json, xml} object from _convertBlockForScratchBlocks.
 * @param {string} extendedOpcode - Full opcode including extension id prefix.
 * @param {object} theme          - Current GUI theme.
 */
const defineExtendableBlock = (ScratchBlocks, categoryInfo, blockInfoEntry, extendedOpcode, theme) => {
    if (!ScratchBlocks.defineExtendableBlock) {
        // Fallback: older scratch-blocks build without the utility.
        // Register via JSON so the block at least appears (without expand buttons).
        if (blockInfoEntry.json) {
            ScratchBlocks.defineBlocksWithJsonArray([
                injectExtensionBlockTheme(blockInfoEntry.json, theme)
            ]);
        }
        return;
    }

    const blockInfo = blockInfoEntry.info;
    const ext = blockInfo.extendable || {};
    const colors = injectExtensionBlockTheme(blockInfoEntry.json, theme);

    // Resolve outputShape / output from blockType.
    let outputShape = ScratchBlocks.OUTPUT_SHAPE_SQUARE;
    let output = null;
    switch (blockInfo.blockType) {
    case BlockType.REPORTER:
        outputShape = ScratchBlocks.OUTPUT_SHAPE_ROUND;
        output = 'String';
        break;
    case BlockType.BOOLEAN:
        outputShape = ScratchBlocks.OUTPUT_SHAPE_HEXAGONAL;
        output = 'Boolean';
        break;
    case BlockType.COMMAND:
        outputShape = ScratchBlocks.OUTPUT_SHAPE_SQUARE;
        output = null;
        break;
    default: {
        const shapeDef = BlockShapeRegistry.getForBlockType(blockInfo.blockType);
        if (shapeDef) {
            outputShape = shapeDef.outputShape;
            output = shapeDef.output || null;
        }
        break;
    }
    }

    ScratchBlocks.defineExtendableBlock({
        opcode:          extendedOpcode,
        outputShape,
        output,
        colour:          colors.colour,
        colourSecondary: colors.colourSecondary,
        colourTertiary:  colors.colourTertiary,
        prefixText:      ext.prefixText  || null,
        itemLabel:       ext.itemLabel   !== undefined ? ext.itemLabel : null,
        inputCheck:      ext.inputCheck  || null,
        inputName:       ext.inputName   || 'ITEM',
        shadowType:      ext.shadowType  || null,
        shadowField:     ext.shadowField || null,
        shadowValue:     ext.shadowValue !== undefined ? ext.shadowValue : '',
        minItems:        ext.minItems    !== undefined ? ext.minItems    : 1,
        defaultItems:    ext.defaultItems !== undefined ? ext.defaultItems : 2,
        extensions:      blockInfo.extensions || []
    });
};

export default defineExtendableBlock;
